import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { UserLevel } from '@app/core/models/user-level';
import { MenuService } from '@app/menu/services/menu.service';
import { RouterService } from '@app/routing/router.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'portal-search-input',
  templateUrl: './search-input.component.html',
  host: {
    class: 'search-input',
  },
  standalone: false,
})
export class SearchInputComponent implements OnInit {
  @ViewChild('dropdown', { static: false })
  public dropdown: NgbDropdown;

  @ViewChild('input', { static: true })
  public input: ElementRef<HTMLInputElement>;

  public activeIndex: number = 0;
  public query: string = '';

  public showEnterprise: boolean = false;
  public showServiceProvider: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private menuService: MenuService,
    private routerService: RouterService,
    private router: Router
  ) {}

  ngOnInit() {
    this.menuService.menuData$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateState();
    });

    this.authenticationService.isAuthenticated$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateState();
    });
  }

  public onChange() {
    this.query = this.input.nativeElement.value;

    if (this.query.length > 0) {
      this.dropdown.open();
    } else {
      this.dropdown.close();
    }
  }

  /**
   * Handle arrow down and arrow up events (i.e. changing the highlighted index accordingly)
   * @param event
   */
  public onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();

      // Make sure that the currently selected entry isn't the last one
      // Switching between entries is only possible if both entries are displayed
      if (this.showServiceProvider && this.showEnterprise && this.activeIndex < 1) {
        ++this.activeIndex;
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();

      // Make sure that the currently selected entry isn't the first one
      // Switching between entries is only possible if both entries are displayed
      if (this.showServiceProvider && this.showEnterprise && this.activeIndex > 0) {
        --this.activeIndex;
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();

      if (this.activeIndex === 0) {
        // Enterprise is first
        this.submit('enterprise');
      } else {
        this.submit('reseller');
      }
    }
  }

  public submit(type: 'enterprise' | 'reseller') {
    const value = this.input.nativeElement.value;

    if (value.length > 1) {
      let url: string;

      // Resolve the url based on the given type
      if (type === 'enterprise') {
        url = this.routerService.resolveLink('search-results-for-enterprise').url;
      } else {
        url = this.routerService.resolveLink('search-results-for-reseller').url;
      }

      // Create the url tree with the query parameter included
      const urlTree = this.router.createUrlTree([url, { query: value }]);

      // Navigate to the created url tree
      this.routerService.navigateByUrl(urlTree);

      this.dropdown.close();
    }
  }

  private updateState() {
    this.showEnterprise = this.menuService.isActive(UserLevel.EnterpriseAdmin);
    this.showServiceProvider = this.authenticationService.userIsServiceProviderAdmin();

    if (this.showEnterprise === true) {
      this.activeIndex = 0;
    } else {
      this.activeIndex = 1;
    }
  }
}
